import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import CodeWindow from '../components/CodeWindow';
import ProjectPreview from '../components/ProjectPreview';
import CTA from '../components/CTA';
import services from '../data/services';
import projects from '../data/portfolio';
import { postList } from '../data/posts';
import './Home.css';

const marquee = [
  'React', 'Next.js', 'TypeScript', 'Node.js', 'Python', 'Django', 'Swift', 'Kotlin',
  'React Native', 'Flutter', 'PostgreSQL', 'MongoDB', 'Redis', 'GraphQL', 'AWS', 'Azure',
  'Google Cloud', 'Kubernetes', 'Docker', 'Terraform',
];

const process = [
  { step: '01', title: 'Discover', description: 'We dig into your goals, users and constraints, then turn them into a scoped plan with clear milestones.' },
  { step: '02', title: 'Design', description: 'Architecture and UX designed together, so what looks good is also fast, secure and maintainable.' },
  { step: '03', title: 'Build', description: 'Short agile cycles, weekly demos and reviewed, tested code. You see progress, not status reports.' },
  { step: '04', title: 'Ship & scale', description: 'Automated deploys, monitoring and ongoing support, because launch day is the start, not the finish.' },
];

const principles = [
  { icon: 'sparkles', title: 'Innovative Solutions', description: 'We leverage the latest technologies and best practices to deliver solutions that drive real business value.' },
  { icon: 'zap', title: 'Fast Delivery', description: 'Our agile methodology ensures rapid development cycles and timely delivery of high-quality software.' },
  { icon: 'users', title: 'Expert Team', description: 'Our experienced developers bring years of expertise across multiple technologies and industries.' },
  { icon: 'shield', title: 'Secure & Scalable', description: 'We build secure, scalable applications that grow with your business and protect your data.' },
  { icon: 'layers', title: 'Full-Service', description: 'From concept to deployment, we provide end-to-end development services tailored to your needs.' },
  { icon: 'wrench', title: 'Dedicated Support', description: "Our commitment doesn't end at launch. We provide ongoing support and maintenance for every project." },
];

const Home = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ESCR LLC',
    url: 'https://escr.online',
    logo: 'https://escr.online/logo.png',
    description: 'ESCR LLC delivers cutting-edge software solutions that transform businesses. Expert web development, mobile apps, and cloud solutions.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US'
    },
    sameAs: [
      // Add your social media profiles here
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'info@escr.online',
      telephone: '+381-60-6500-893'
    }
  };

  const featured = projects.find((p) => p.featured) || projects[0];
  const latestPosts = postList.slice(0, 3);

  return (
    <div className="home">
      <SEO
        title="Home"
        description="ESCR LLC delivers cutting-edge software solutions that transform businesses. We combine innovation, expertise, and dedication to bring your vision to life."
        keywords="software development, web development, mobile apps, cloud solutions, software consulting, ESCR LLC"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="hero">
        <div className="grid-bg" aria-hidden="true" />
        <div className="glow hero-glow-a" aria-hidden="true" />
        <div className="glow hero-glow-b" aria-hidden="true" />
        <div className="container hero-inner">
          <div className="hero-copy">
            <Link to="/contact" className="hero-badge">
              <span className="status-dot" aria-hidden="true" />
              Available for new projects
              <Icon name="arrowRight" />
            </Link>
            <h1 className="hero-title">
              We engineer software
              <br />
              <span className="text-gradient">that actually ships.</span>
            </h1>
            <p className="hero-description">
              ESCR LLC delivers cutting-edge software solutions that transform businesses. Web platforms, mobile apps
              and cloud infrastructure, built by senior engineers who care about the details.
            </p>
            <div className="btn-row">
              <Link to="/contact" className="btn btn-primary btn-large">
                Start a project
                <Icon name="arrowRight" />
              </Link>
              <Link to="/portfolio" className="btn btn-secondary btn-large">
                View our work
              </Link>
            </div>
            <ul className="hero-disciplines" aria-label="What we do">
              {services.map((s) => (
                <li key={s.slug}>
                  <Icon name={s.icon} />
                  {s.name}
                </li>
              ))}
            </ul>
          </div>
          <div className="hero-visual">
            <CodeWindow />
          </div>
        </div>
      </section>

      {/* Tech marquee */}
      <section className="marquee" aria-label="Technologies we work with">
        <div className="marquee-track">
          {[0, 1].map((copy) => (
            <ul className="marquee-list" key={copy} aria-hidden={copy === 1}>
              {marquee.map((tech) => (
                <li key={tech}>{tech}</li>
              ))}
            </ul>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="section" id="services">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">Services</span>
            <h2 className="section-title">
              One team for the whole stack,
              <span className="text-muted"> from first commit to production.</span>
            </h2>
          </Reveal>
          <div className="bento">
            {services.map((service, i) => (
              <Reveal key={service.slug} delay={i * 80} className={`bento-cell bento-${i}`}>
                <Link to={`/services/${service.slug}`} className="card bento-card">
                  <div className="bento-top">
                    <span className="icon-tile">
                      <Icon name={service.icon} />
                    </span>
                    <span className="bento-arrow">
                      <Icon name="arrowUpRight" />
                    </span>
                  </div>
                  <div className="bento-body">
                    <h3 className="bento-title">{service.name}</h3>
                    <p className="bento-desc">{service.short}</p>
                    <div className="bento-tags">
                      {service.stack.flatMap((group) => group.items).slice(0, i < 2 ? 6 : 4).map((item) => (
                        <span className="tag" key={item}>{item}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section process-section">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">How we work</span>
            <h2 className="section-title">A process built for momentum.</h2>
            <p className="section-lead">
              No black boxes. You get a predictable rhythm, working software every sprint and a team that tells you
              the truth about scope.
            </p>
          </Reveal>
          <ol className="process">
            {process.map((item, i) => (
              <Reveal as="li" key={item.step} delay={i * 100} className="process-step">
                <span className="process-num">{item.step}</span>
                <span className="process-line" aria-hidden="true" />
                <h3 className="process-title">{item.title}</h3>
                <p className="process-desc">{item.description}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Principles */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">Why ESCR</span>
            <h2 className="section-title">Why teams choose ESCR LLC</h2>
          </Reveal>
          <div className="principles">
            {principles.map((item, i) => (
              <Reveal key={item.title} delay={(i % 3) * 80} className="card principle">
                <span className="icon-tile">
                  <Icon name={item.icon} />
                </span>
                <h3 className="principle-title">{item.title}</h3>
                <p className="principle-desc">{item.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured work */}
      {featured && (
        <section className="section">
          <div className="container">
            <Reveal className="section-head-row">
              <div className="section-head">
                <span className="eyebrow">Selected work</span>
                <h2 className="section-title">Recent launches</h2>
              </div>
              <Link to="/portfolio" className="link-arrow">
                All projects <Icon name="arrowRight" />
              </Link>
            </Reveal>
            <Reveal>
              <Link to={`/portfolio/${featured.id}`} className="card featured">
                <div className="featured-copy">
                  <div className="featured-meta">
                    <span className="tag tag-accent">{featured.category}</span>
                    <span className="tag">{featured.date}</span>
                  </div>
                  <h3 className="featured-title">{featured.title}</h3>
                  <p className="featured-desc">{featured.description}</p>
                  <div className="featured-tags">
                    {featured.technologies.map((tech) => (
                      <span className="tag" key={tech}>{tech}</span>
                    ))}
                  </div>
                  <span className="link-arrow featured-link">
                    Read case study <Icon name="arrowRight" />
                  </span>
                </div>
                <div className="featured-visual">
                  <ProjectPreview project={featured} />
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Blog */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head-row">
            <div className="section-head">
              <span className="eyebrow">Journal</span>
              <h2 className="section-title">Notes from the codebase</h2>
            </div>
            <Link to="/blogs" className="link-arrow">
              Read the blog <Icon name="arrowRight" />
            </Link>
          </Reveal>
          <div className="home-posts">
            {latestPosts.map((post, i) => (
              <Reveal key={post.id} delay={i * 80}>
                <Link to={`/blogs/${post.id}`} className="card home-post">
                  <div className="home-post-meta">
                    <span>{post.category}</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="home-post-title">{post.title}</h3>
                  <p className="home-post-excerpt">{post.excerpt}</p>
                  <span className="link-arrow">
                    Read article <Icon name="arrowRight" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTA title="Ready to start your project?" />
    </div>
  );
};

export default Home;
